import {Client as StompClient, over as overStomp} from 'stompjs';
import * as SockJS from 'sockjs-client';
import {fromEvent, interval, Observable, Subscription, timer} from 'rxjs';
import {filter, timeout} from 'rxjs/operators';
import {UserWsInfo} from './model/user-ws-info';

const SOCKJS_TIMEOUT = 60 * 1000;
const USER_INFO_TIMEOUT = 8 * 1000;
const CONNECT_DEADLINE = 30 * 1000;
const VISIBLE_RECONNECT_DELAY = 1000;
const CONNECTION_CHECK_INTERVAL = 40 * 1000;
const HEARTBEAT_EVERY_MINUTE = 60 * 1000;
const JITTER_FLOOR = 0.5;

export interface SocketOptions {
  hiddenReconnectDelay: number;
  canConnect: () => boolean;
  fetchUserInfo: () => Observable<UserWsInfo>;
  headers: (userInfo: UserWsInfo) => object;
  onConnected: (userInfo: UserWsInfo) => void;
  onResume?: () => void;
  isDataFlowing?: () => boolean;
  beforeConnect?: () => void;
}

export class ReconnectingSocket {

  private client: StompClient;
  private wanted = false;
  private connecting = false;
  private connectingSince = 0;
  private attempts = 0;
  private reconnectPending = false;
  private checkerSubs: Subscription;
  private visibilitySubs: Subscription;
  private onlineSubs: Subscription;
  private userInfoSubs: Subscription;
  private reconnectSubs: Subscription;

  constructor(private options: SocketOptions) {
    this.checkerSubs = interval(CONNECTION_CHECK_INTERVAL).subscribe(() => this.checkConnection());
    this.visibilitySubs = fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'))
      .subscribe(() => this.resume());
    this.onlineSubs = fromEvent(window, 'online').subscribe(() => this.resume());
  }

  public connect(): void {
    if (this.options.canConnect()) {
      this.wanted = true;
      this.markConnecting();
      if (this.options.beforeConnect) {
        this.options.beforeConnect();
      }
      this.requestUserInfo();
    }
  }

  public disconnect(): void {
    this.wanted = false;
    this.connecting = false;
    this.attempts = 0;
    this.reconnectPending = false;
    this.unsub(this.userInfoSubs);
    this.unsub(this.reconnectSubs);
    this.closeClient();
  }

  public reconnectNow(): void {
    this.connecting = false;
    this.closeClient();
    this.scheduleReconnect();
  }

  public destroy(): void {
    this.unsub(this.checkerSubs);
    this.unsub(this.visibilitySubs);
    this.unsub(this.onlineSubs);
    this.disconnect();
  }

  public isConnected(): boolean {
    return this.client != null && this.client.connected;
  }

  public send(destination: string, body?: string): void {
    if (this.isConnected()) {
      this.client.send(destination, {}, body);
    }
  }

  public subscribe(destination: string, handler: (event: any) => void): void {
    this.client.subscribe(destination, handler);
  }

  private requestUserInfo(): void {
    let received = false;
    this.unsub(this.userInfoSubs);
    this.userInfoSubs = this.options.fetchUserInfo()
      .pipe(timeout(USER_INFO_TIMEOUT))
      .subscribe({
        next: userInfo => {
          received = true;
          this.openSocket(userInfo);
        },
        error: () => this.userInfoFailed(),
        complete: () => {
          if (!received) {
            this.userInfoFailed();
          }
        }
      });
  }

  private userInfoFailed(): void {
    this.connecting = false;
    this.scheduleReconnect();
  }

  private openSocket(userInfo: UserWsInfo): void {
    this.closeClient();

    const ws = new SockJS(userInfo.url, null, {timeout: SOCKJS_TIMEOUT});
    this.client = overStomp(ws);
    this.client.heartbeat.outgoing = HEARTBEAT_EVERY_MINUTE;
    this.client.heartbeat.incoming = HEARTBEAT_EVERY_MINUTE;
    this.client.debug = () => {};

    this.client.connect(this.options.headers(userInfo), () => {
      if (this.wanted) {
        this.connecting = false;
        this.attempts = 0;
        this.options.onConnected(userInfo);
      }
    }, () => this.connectionLost());
  }

  private connectionLost(): void {
    this.connecting = false;
    this.closeClient();
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.wanted) {
      const backoff = document.visibilityState === 'visible'
        ? Math.min(VISIBLE_RECONNECT_DELAY * Math.pow(2, this.attempts), this.options.hiddenReconnectDelay)
        : this.options.hiddenReconnectDelay;
      const wait = Math.round(backoff * (JITTER_FLOOR + Math.random() * (1 - JITTER_FLOOR)));
      this.attempts++;
      this.unsub(this.reconnectSubs);
      this.reconnectPending = true;
      this.reconnectSubs = timer(wait).subscribe(() => {
        this.reconnectPending = false;
        this.connect();
      });
    }
  }

  private resume(): void {
    if (this.wanted) {
      if (this.isConnected() && this.dataIsFlowing()) {
        if (this.options.onResume) {
          this.options.onResume();
        }
      } else {
        this.unsub(this.reconnectSubs);
        this.reconnectPending = false;
        this.attempts = 0;
        this.connecting = false;
        this.closeClient();
        this.connect();
      }
    }
  }

  private checkConnection(): void {
    const idle = this.wanted && !this.isConnecting() && !this.reconnectPending;
    if (idle && !this.isConnected()) {
      this.reconnectNow();
    }
  }

  private dataIsFlowing(): boolean {
    return this.options.isDataFlowing ? this.options.isDataFlowing() : true;
  }

  private markConnecting(): void {
    this.connecting = true;
    this.connectingSince = Date.now();
  }

  private isConnecting(): boolean {
    return this.connecting && (Date.now() - this.connectingSince) < CONNECT_DEADLINE;
  }

  private closeClient(): void {
    if (this.client != null) {
      try {
        this.client.disconnect();
      } catch (error) {
        this.closeSocketQuietly();
      }
    }
    this.client = null;
  }

  private closeSocketQuietly(): void {
    const socket = (this.client as any).ws;
    if (socket != null) {
      socket.onclose = null;
      socket.close();
    }
  }

  private unsub(subscription: Subscription): void {
    if (subscription) {
      subscription.unsubscribe();
    }
  }

}
